/**
 * FieldHint — a form label with a small "?" button that reveals a sentence of
 * explanation directly underneath it.
 *
 * Why this exists next to `InfoHint`: that one shows its text in a popover
 * floating over the page, centred on its trigger. Inside a dialog the panel
 * scrolls (`overflow-y-auto`), which makes the sideways axis clip as well, so a
 * popover hanging past the panel's left edge is cut off with no way to scroll
 * to it. This keeps the explanation in normal flow, where it cannot be clipped
 * and it reflows on a phone.
 *
 * The trigger is a real button — reachable by keyboard, named for screen
 * readers, and tied to the text it reveals with `aria-expanded` /
 * `aria-controls`. Never a hover-only `title`, which a keyboard or touch user
 * can never see.
 *
 * The hint's id is derived from the control's own id (`<id>-hint`) rather than
 * generated, so the control can point `aria-describedby` at it. The paragraph
 * is always in the DOM and merely `hidden` when collapsed: a description
 * reference is followed even into hidden content, so a screen reader reads the
 * explanation with the field either way, while the sighted reader gets a form
 * that is three lines shorter until they ask.
 *
 * Four ways in, because no one of them reaches everybody:
 *
 * 1. **Resting a mouse on it** shows the hint after a short pause, and moving
 *    away hides it again — the cheapest possible look, with no click to undo.
 *    The pause is what stops the form flickering as the pointer crosses the
 *    row on its way somewhere else.
 * 2. **Clicking pins it open** until it is clicked again, for reading at
 *    leisure, copying out of, or comparing against what is typed in the box.
 *    The glyph darkens while it is pinned so it is obvious which one is stuck
 *    open and how to put it back.
 * 3. **Keyboard focus** shows it immediately — there is no hovering with a
 *    keyboard, and the hint must not be mouse-only. Escape dismisses it
 *    without disturbing the dialog around it.
 * 4. **Touch** has no hover at all, so a tap goes straight to the pinned state.
 *    Hover is deliberately ignored for touch pointers: a tap fires a synthetic
 *    "enter" before the click, and honouring it would leave the hint showing
 *    after the tap that was meant to close it.
 *
 * The focus ring is `focus-visible` only. A plain `focus:` ring stays drawn on
 * the last glyph anybody clicked, which reads as a selected state that nothing
 * ever clears; keyboard users still need to see where they are, so the ring is
 * kept for them and given enough contrast to satisfy WCAG 2.4.11.
 */
import { useEffect, useRef, useState } from 'react'

/** How long a pointer rests on the "?" before the hint appears. */
const HOVER_DELAY_MS = 400

interface FieldHintProps {
  /** `id` of the control this labels. */
  htmlFor: string
  /** The visible label text. */
  label: React.ReactNode
  /** Accessible name for the "?" button, e.g. "What the subject box does". */
  hintLabel: string
  /** The explanation, shown when the button is pressed. */
  children: React.ReactNode
}

export default function FieldHint({ htmlFor, label, hintLabel, children }: FieldHintProps) {
  // Two separate reasons the hint can be showing, because they end differently:
  // a hover or a focus is *transient* and ends when the pointer or the focus
  // leaves, while a click *pins* it until it is clicked again. Collapsing these
  // into one boolean is what makes "click to close" fail — the pointer is still
  // sitting on the button, so a single flag is immediately set true again.
  const [pinned, setPinned] = useState(false)
  const [transient, setTransient] = useState(false)
  const timer = useRef<number | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)

  const hintId = `${htmlFor}-hint`
  const open = pinned || transient

  function cancelTimer() {
    if (timer.current !== null) {
      window.clearTimeout(timer.current)
      timer.current = null
    }
  }

  // A pending reveal must not fire after the field has gone — the dialog this
  // lives in unmounts on close.
  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current)
    },
    []
  )

  /** Mouse and pen hover. Touch is excluded — see the note at the top. */
  function hoverable(pointerType: string) {
    return pointerType === 'mouse' || pointerType === 'pen'
  }

  function handlePointerEnter(e: React.PointerEvent) {
    if (!hoverable(e.pointerType)) return
    cancelTimer()
    timer.current = window.setTimeout(() => setTransient(true), HOVER_DELAY_MS)
  }

  function handlePointerLeave(e: React.PointerEvent) {
    if (!hoverable(e.pointerType)) return
    cancelTimer()
    setTransient(false)
  }

  function handleClick() {
    cancelTimer()
    const next = !pinned
    setPinned(next)
    // Unpinning also has to drop the hover/focus reveal, or the click that was
    // meant to close it leaves it open under the pointer that is still there.
    if (!next) setTransient(false)
  }

  /**
   * Escape dismisses the hint — but only while it is showing, so Escape still
   * closes the dialog the rest of the time.
   *
   * Two details this cannot be written without. It has to be a **native**
   * listener on the button rather than a React `onKeyDown`: React runs its
   * handlers from the app's root element, which is an ancestor of the dialog
   * panel, so by the time a React handler saw the key the panel's own listener
   * would already have closed the dialog. And it claims the key with
   * `preventDefault()` *and* `stopPropagation()`. Either would do on its own —
   * the panel checks `defaultPrevented` and leaves a key alone once something
   * inside has acted on it, and stopping the bubble means it never sees the key
   * at all — but they fail in opposite directions if the panel is ever rewired,
   * so both are set.
   *
   * Scoped to the button rather than to the document, which is the important
   * part: Escape typed in the subject or the message body still closes the
   * dialog, even with a hint pinned open somewhere above.
   */
  useEffect(() => {
    const el = buttonRef.current
    if (!el || !open) return
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopPropagation()
      if (timer.current !== null) {
        window.clearTimeout(timer.current)
        timer.current = null
      }
      setPinned(false)
      setTransient(false)
    }
    el.addEventListener('keydown', onKeyDown)
    return () => el.removeEventListener('keydown', onKeyDown)
  }, [open])

  return (
    <>
      <div className="flex items-center gap-1 mb-1">
        <label className="label mb-0" htmlFor={htmlFor}>{label}</label>
        <button
          ref={buttonRef}
          type="button"
          aria-label={hintLabel}
          aria-expanded={open}
          aria-controls={hintId}
          onClick={handleClick}
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerLeave}
          onFocus={() => setTransient(true)}
          onBlur={() => { cancelTimer(); setTransient(false) }}
          // The circle stays 16px, but the button box is 24px so the hit area
          // meets the WCAG 2.5.8 minimum without the design growing.
          // `outline-none` rather than nothing: it leaves a transparent outline
          // behind, which is what keeps the focus ring visible in Windows
          // high-contrast mode where box-shadow rings are dropped.
          className={`inline-flex h-6 w-6 items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
            pinned ? 'text-gray-700' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <span
            aria-hidden="true"
            className={`flex h-4 w-4 items-center justify-center rounded-full border text-[10px] font-bold leading-none ${
              pinned
                ? 'border-gray-500 bg-gray-100'
                : 'border-gray-300 hover:bg-gray-100'
            }`}
          >
            ?
          </span>
        </button>
      </div>
      <p id={hintId} hidden={!open} className="text-xs text-gray-500 mb-1 leading-relaxed">
        {children}
      </p>
    </>
  )
}
